import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  DollarSign,
  Heart,
  Activity,
  ShoppingBag,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useCategories, Category } from "@/hooks/use-categories";

const CategoryCard = ({
  icon,
  title,
  description,
  path,
  longDescription,
  index,
}: Category & { index: number }) => (
  <div
    className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-500 hover:translate-y-[-8px] animate-fade-in opacity-0"
    style={{ animationDelay: `${0.2 + index * 0.1}s` }}
  >
    <div className="p-8 flex flex-col h-full">
      <div
        className="w-16 h-16 mb-6 rounded-full bg-[#1A2B4D]/10 flex items-center justify-center text-[#1A2B4D] animate-fade-in opacity-0"
        style={{ animationDelay: `${0.3 + index * 0.1}s` }}
      >
        {icon}
      </div>
      <h3 className="text-2xl font-bold text-[#1A2B4D] mb-4">{title}</h3>
      <p className="text-[#1A2B4D]/70 mb-6">{description}</p>
      <p className="text-[#1A2B4D]/80 mb-8 flex-grow">{longDescription}</p>
      <Link to={path} className="mt-auto">
        <Button className="w-full bg-[#1A2B4D] hover:bg-[#2E3F66] text-white px-6 py-5 h-auto text-lg shadow-lg hover:shadow-xl transition-all duration-300 group border border-transparent hover:border-gold">
          <span>Explore {title}</span>
          <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-2" />
        </Button>
      </Link>
    </div>
  </div>
);

const Categories = () => {
  const { categories, isLoading } = useCategories();

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-20">
        {/* Hero section */}
        <section className="relative py-16 md:py-24 bg-[#1A2B4D] overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-gold/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-gold/10 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl"></div>

          <div className="container mx-auto px-4 md:px-6 relative">
            <div className="max-w-3xl mx-auto text-center">
              <span
                className="inline-block px-3 py-1 bg-white/10 text-white text-sm rounded-full mb-4 animate-slide-in-up opacity-0"
                style={{ animationDelay: "0.1s" }}
              >
                Categories
              </span>
              <h1
                className="text-4xl md:text-5xl font-bold text-white mb-6 animate-slide-in-up opacity-0"
                style={{ animationDelay: "0.2s" }}
              >
                Explore Our Content Categories
              </h1>
              <p
                className="text-xl text-white/90 leading-relaxed animate-slide-in-up opacity-0"
                style={{ animationDelay: "0.3s" }}
              >
                Discover curated content designed to inspire and enhance every
                aspect of your life
              </p>
            </div>
          </div>
        </section>

        {/* Categories Grid */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 md:px-6">
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[1, 2, 3, 4].map((_, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden shadow-sm animate-pulse"
                  >
                    <div className="p-8">
                      <div className="w-16 h-16 mb-6 rounded-full bg-[#1A2B4D]/10"></div>
                      <div className="h-8 bg-[#1A2B4D]/10 rounded mb-4 w-1/3"></div>
                      <div className="h-4 bg-[#1A2B4D]/10 rounded mb-2"></div>
                      <div className="h-4 bg-[#1A2B4D]/10 rounded mb-6"></div>
                      <div className="h-4 bg-[#1A2B4D]/10 rounded mb-2"></div>
                      <div className="h-4 bg-[#1A2B4D]/10 rounded mb-2"></div>
                      <div className="h-4 bg-[#1A2B4D]/10 rounded mb-8 w-2/3"></div>
                      <div className="h-12 bg-[#1A2B4D]/10 rounded-md"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {categories.map((category, index) => (
                  <CategoryCard key={index} {...category} index={index} />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Categories;
